import { DownloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Drawer, Flex, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Schemas, type Warehouse } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { usePoll } from "@/components/tabs/WorkTabs";

type PDAList = Awaited<ReturnType<typeof fetchPDAs>>;
type PDA = PDAList["items"][number];

const fetchPDAs = (warehouseId: string) => unwrap(api.GET("/v1/warehouses/{warehouseID}/pdas", { params: { path: { warehouseID: warehouseId } } }));

function online(d: PDA) {
  return d.status === "active" && !!d.lastSeenAt && Date.parse(d.lastSeenAt) > Date.now() - 10 * 60_000;
}

/**
 * Perangkat Android: PDAs running the Zerpid warehouse app in this warehouse.
 * Each is paired once with a code typed into the app; staff then sign in on
 * it with their ID and PIN (User & Role). Removing one signs them out.
 */
export function PdasDrawer({ warehouse, onClose }: { warehouse: Warehouse | null; onClose: () => void }) {
  const { t, formatDateTime } = useI18n();
  const { me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState<Schemas["RecorderPairing"] | null>(null);
  const key = ["pdas", warehouse?.id];
  const list = useQuery({ queryKey: key, queryFn: () => fetchPDAs(warehouse!.id), enabled: warehouse !== null, refetchInterval: usePoll(30_000) });
  const revoke = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/pdas/{deviceID}/revoke", { params: { path: { deviceID: id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Drawer
      open={warehouse !== null}
      onClose={onClose}
      size="large"
      title={warehouse ? t("pda.title", { name: warehouse.name }) : ""}
      destroyOnHidden
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)} disabled={!warehouse?.wmsEnabled}>
          {t("pda.add")}
        </Button>
      }
    >
      <Typography.Paragraph type="secondary">{t("pda.help")}</Typography.Paragraph>
      {warehouse && !warehouse.wmsEnabled && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("pda.noWms")} />}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        title={t("pda.install")}
        description={t("pda.installSteps")}
        action={
          <Button icon={<DownloadOutlined />} href={list.data?.download ?? "/downloads/zerpid-gudang.apk"}>
            {t("pda.download", { version: list.data?.latestVersion ?? "" })}
          </Button>
        }
      />
      {(list.data?.pairings ?? []).length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("rec.pending", { names: (list.data?.pairings ?? []).map((p) => p.name).join(", ") })}
        />
      )}
      <Table<PDA>
        rowKey="id"
        size="small"
        loading={list.isPending}
        dataSource={list.data?.items ?? []}
        pagination={false}
        locale={{ emptyText: t("pda.empty") }}
        columns={[
          {
            title: t("rec.device"),
            render: (_, d) => (
              <Space orientation="vertical" size={0}>
                <Space size={6}>
                  <Typography.Text strong>{d.name}</Typography.Text>
                  {d.status === "revoked" ? <Tag>{t("rec.revoked")}</Tag> : online(d) ? <Tag color="success">{t("rec.online")}</Tag> : <Tag>{t("rec.offline")}</Tag>}
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {[d.model, d.appVersion && `v${d.appVersion}`].filter(Boolean).join(" · ") || "—"}
                </Typography.Text>
              </Space>
            ),
          },
          { title: t("pda.lastUser"), dataIndex: "lastUserName", width: 160, render: (v: string) => v || "—" },
          { title: t("rec.lastSeen"), dataIndex: "lastSeenAt", width: 170, render: (v: string | null) => (v ? formatDateTime(v, tz) : "—") },
          {
            width: 100,
            render: (_, d) =>
              d.status === "active" && (
                <Popconfirm title={t("pda.revokeConfirm", { name: d.name })} onConfirm={() => revoke.mutate(d.id)}>
                  <Button size="small" danger>
                    {t("rec.revoke")}
                  </Button>
                </Popconfirm>
              ),
          },
        ]}
      />
      <AddModal
        open={adding}
        warehouseId={warehouse?.id ?? ""}
        onClose={() => setAdding(false)}
        onCode={(c) => {
          setAdding(false);
          setCode(c);
          void queryClient.invalidateQueries({ queryKey: key });
        }}
      />
      <Modal open={code !== null} title={t("rec.codeTitle", { name: code?.name ?? "" })} footer={<Button onClick={() => setCode(null)}>{t("common.close")}</Button>} onCancel={() => setCode(null)}>
        <Flex vertical align="center" gap={8} style={{ margin: "16px 0" }}>
          <Typography.Text className="z-num" style={{ fontSize: 36, fontWeight: 700, letterSpacing: 4 }} copyable>
            {code?.code}
          </Typography.Text>
          <Typography.Text type="secondary">{code && t("rec.codeExpires", { at: formatDateTime(code.expiresAt, tz) })}</Typography.Text>
        </Flex>
        <Typography.Paragraph>{t("pda.codeSteps")}</Typography.Paragraph>
      </Modal>
    </Drawer>
  );
}

function AddModal({ open, warehouseId, onClose, onCode }: { open: boolean; warehouseId: string; onClose: () => void; onCode: (c: Schemas["RecorderPairing"]) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const [form] = Form.useForm<{ name: string }>();
  const make = useMutation({
    mutationFn: (v: { name: string }) =>
      unwrap(api.POST("/v1/warehouses/{warehouseID}/pdas/pairing", { params: { path: { warehouseID: warehouseId } }, body: { name: v.name.trim() } })),
    onSuccess: onCode,
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal open={open} title={t("pda.add")} okText={t("rec.makeCode")} cancelText={t("common.cancel")} confirmLoading={make.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form form={form} layout="vertical" requiredMark={false} onFinish={(v) => make.mutate(v)}>
        <Form.Item name="name" label={t("rec.deviceName")} extra={t("pda.nameHelp")} rules={[{ required: true, message: t("field.required") }]}>
          <Input maxLength={80} placeholder={t("pda.namePlaceholder")} autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
}
